// Full-board undo/redo over a widget board whose source of truth lives in the
// CONSUMER's store. This hook never owns the board: it records snapshots of
// the committed `board` prop and hands a restored board back through `commit`
// — ONE op the consumer routes to its own persistence (whatever staging,
// forking, or write-through semantics that store implements).
//
// Wiring:
//   - `handleLayoutChange` → the grid's full-layout `onLayoutChange`. It
//     RECORDS only — the grid commit already flows to persistence through the
//     grid's `onMove`, so committing here too would double-persist.
//   - `snapshot()` → call immediately BEFORE a board-membership op the host
//     initiates (add, duplicate, replace, delete, seed). Records the
//     pre-op board unconditionally.
//   - `apply(nextLayout)` → programmatic placement changes (tidy/pack):
//     records, then commits the placements through the consumer.
//   - `undo`/`redo` → restore a snapshot via `commit`, rebuilt against the
//     live board by `restoreBoardSnapshot` (config edits survive; membership
//     and placements roll back).
//
// The stacks reset when `resetKey` changes — a different key is a different
// board, and its snapshots must not leak across. Pair with
// `useGridHistoryHotkeys` for the keyboard bindings.

import * as React from "react";

import { useSnapshotHistory } from "../../hooks/use-snapshot-history.ts";
import type { Omittable } from "../../lib/props";
import type { GridLayoutItem } from "../sortable-grid";
import type { WidgetInstance } from "./types";
import {
  applyPlacements,
  restoreBoardSnapshot,
  samePlacements,
} from "./widget-board-history";

export interface WidgetBoardHistory {
  /** Wire to the grid's `onLayoutChange`. Records the pre-change board
   *  (skipping no-op gestures); does NOT commit. */
  handleLayoutChange: (next: GridLayoutItem[]) => void;
  /** Record the current board before a host-initiated membership op
   *  (add/duplicate/replace/delete/seed). */
  snapshot: () => void;
  /** Record the current board, then commit `next`'s placements — for
   *  programmatic layout changes (Tidy) that don't flow through a gesture. */
  apply: (next: GridLayoutItem[]) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

// Copies: the grid mutates placements in place mid-gesture.
const cloneBoard = (board: WidgetInstance[]) => board.map((i) => ({ ...i }));

export function useWidgetBoardHistory(
  options: Omittable<{
    /** The current committed board (store truth). */
    board: WidgetInstance[];
    /** The consumer's persistence path for a restored/applied board. */
    commit: (next: WidgetInstance[]) => void;
    /** Clears the stacks when it changes — pass the board's identity key. */
    resetKey: string;
    /** Max undo depth. Defaults to 50. */
    limit?: number;
  }>,
): WidgetBoardHistory {
  const { board, commit, resetKey, limit } = options;

  const boardRef = React.useRef(board);
  const commitRef = React.useRef(commit);
  React.useLayoutEffect(() => {
    boardRef.current = board;
    commitRef.current = commit;
  });

  // A restored snapshot is rebuilt against the LIVE board: config edits made
  // since survive, membership and placements roll back.
  const restore = React.useCallback(
    (snap: WidgetInstance[]) =>
      commitRef.current(restoreBoardSnapshot(snap, boardRef.current)),
    [],
  );

  const history = useSnapshotHistory<WidgetInstance[]>({
    value: board,
    commit: restore,
    resetKey,
    clone: cloneBoard,
    ...(limit === undefined ? {} : { limit }),
  });
  const { snapshot, undo, redo, canUndo, canRedo } = history;

  const handleLayoutChange = React.useCallback(
    (next: GridLayoutItem[]) => {
      // A commit that changed nothing (e.g. a drag returned to its origin)
      // must not burn an undo step.
      if (samePlacements(boardRef.current, next)) return;
      snapshot();
    },
    [snapshot],
  );

  const apply = React.useCallback(
    (next: GridLayoutItem[]) => {
      if (samePlacements(boardRef.current, next)) return;
      const restored = applyPlacements(boardRef.current, next);
      snapshot();
      commitRef.current(restored);
    },
    [snapshot],
  );

  return React.useMemo(
    () => ({ handleLayoutChange, snapshot, apply, undo, redo, canUndo, canRedo }),
    [handleLayoutChange, snapshot, apply, undo, redo, canUndo, canRedo],
  );
}
